import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import {
    ActionSection,
    FieldWrappers,
    InputGroup,
    Label,
    ModalHeader,
    Note,
    ScrollableContent,
    SideCancelBtn,
    SubmitBtn,
    Wrapper,
} from "../Styles/InputStyles";
import CategoryCard from "../CategoryCard";
import SelectMultipleStaffModal from "../SelectMultipleStaffModal";
import { capitalizeFirstLetter } from "../../helpers";

const AddCommission = ({
    data,
    staffCommissionLoading,
    handleClose,
    staffCommissionMutate,
    tenantId,
}) => {
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [staffModalOpen, setStaffModalOpen] = useState(false);
    const staffList = data?.staffs || [];
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleStaffSelect = (staffs, setFieldValue, values) => {
        if (staffs && staffs.length > 0) {
            setSelectedStaff(staffs);

            const updatedValues = {};
            staffs.forEach((staff) => {
                const fieldName = `commission_${staff.id}`;
                updatedValues[fieldName] = values[fieldName] || "";
            });

            Object.keys(updatedValues).forEach((key) => {
                setFieldValue(key, updatedValues[key]);
            });

            setStaffModalOpen(false);
        } else {
            setSelectedStaff([]);
        }
    };

    const renderSelectedStaff = (selected = []) => {
        if (!selected || !selected.length) return "Select Staff";

        const maxToShow = 2;
        const namesToShow = selected.slice(0, maxToShow).map((staff) => capitalizeFirstLetter(staff.name));
        const remainingCount = selected.length - maxToShow;

        return (
            <div className="flex text-left">
                <span>{namesToShow.join(", ")}</span>
                {remainingCount > 0 && (
                    <span className="ml-1">+{remainingCount} more</span>
                )}
            </div>
        );
    };

    const generateValidationSchema = (selectedStaff) => {
        const shape = {};

        if (selectedStaff && selectedStaff.length > 0) {
            selectedStaff.forEach((staff) => {
                shape[`commission_${staff.id}`] = Yup.number()
                    .typeError("Commission must be a number")
                    .min(1, "Commission must be greater than 0")
                    .max(500000, "Commission cannot be more than ₹5,00,000")
                    .required(`Commission for ${staff.name} is required`);
            });
        }

        return Yup.object(shape);
    };

    const initialValues = {};

    const handleSubmit = (values, { resetForm }) => {
        const commissionData = {
            staffs: selectedStaff.map((staff) => ({
                staffId: staff.id,
                amount: parseFloat(values[`commission_${staff.id}`]) || 0,
            })),
            propId: data?.occupancy?.propId,
            tenantId: tenantId,
        };

        staffCommissionMutate(commissionData)
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Commission</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={generateValidationSchema(selectedStaff)}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, resetForm, errors, touched }) => {
                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100dvh-120px)]">
                                    <Wrapper>
                                        <InputGroup>
                                            <Label>Select Staff *</Label>
                                            <CategoryCard
                                                title="Staff"
                                                selectedCategory={
                                                    selectedStaff && selectedStaff.length > 0
                                                        ? {
                                                            label: renderSelectedStaff(selectedStaff),
                                                            items: selectedStaff,
                                                        }
                                                        : null
                                                }
                                                onClick={() => setStaffModalOpen(true)}
                                            />
                                            {selectedStaff.length === 0 && touched.staffId && (
                                                <div className="text-red-500 text-sm mt-1">
                                                    Staff is required
                                                </div>
                                            )}
                                        </InputGroup>

                                        {selectedStaff && selectedStaff.length > 0 && (
                                            <>
                                                {selectedStaff.map((staff) => (
                                                    <InputGroup key={staff.id}>
                                                        <Label htmlFor={`commission_${staff.id}`}>
                                                            Commission for {capitalizeFirstLetter(staff.name)}
                                                            {/* {staff.mobile && (
                                                                <span className="text-gray-500 text-sm ml-2">
                                                                    (#{staff.mobile})
                                                                </span>
                                                            )} */}
                                                        </Label>
                                                        <FieldWrappers>
                                                            <Field
                                                                name={`commission_${staff.id}`}
                                                                type="number"
                                                                // step="0.01"
                                                                className="p-2 rounded w-full "
                                                                placeholder={`Enter commission amount for ${capitalizeFirstLetter(staff.name)}`}
                                                            />
                                                        </FieldWrappers>
                                                        <Note>
                                                            Agreed commission with {capitalizeFirstLetter(staff.name)} : {
                                                                staff.commissionType === 0
                                                                    ? `₹${staff.commission || 0}`
                                                                    : staff.commissionType === 1
                                                                        ? `${staff.commission || 0}%`
                                                                        : `Not set`
                                                            }
                                                        </Note>
                                                        {errors[`commission_${staff.id}`] &&
                                                            touched[`commission_${staff.id}`] && (
                                                                <div className="text-red-500 text-sm mt-1">
                                                                    {errors[`commission_${staff.id}`]}
                                                                </div>
                                                            )}
                                                    </InputGroup>
                                                ))}
                                            </>
                                        )}
                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setSelectedStaff([]);
                                            handleClose();
                                        }}
                                    >
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn
                                        type="submit"
                                        disabled={
                                            staffCommissionLoading || selectedStaff.length === 0
                                        }
                                    >
                                        {staffCommissionLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                <SelectMultipleStaffModal
                                    isOpen={staffModalOpen}
                                    onClose={() => setStaffModalOpen(false)}
                                    onSelect={(staff) =>
                                        handleStaffSelect(staff, setFieldValue, values)
                                    }
                                    options={staffList}
                                    selectedItem={selectedStaff}
                                    title="Select Staff"
                                    searchPlaceholder="Search staffs..."
                                    idKey="id"
                                    nameKey="name"
                                    descriptionKeys={["mobile"]}
                                    emptyMessage="No staff available"
                                    noResultsMessage="No staff found"
                                    showClearSelection={true}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;

export default AddCommission;
